import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Button,
} from "reactstrap";
import {
  Card,
  Select,
  InputNumber,
  Button as AntButton,
  Table,
  Space,
  Typography,
  Form,
  Popconfirm,
  message,
  Empty,
  Tag,
  Alert,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import FormTextArea from "../../form-components/TextAreaComponent";
import { FormProvider, useForm } from "react-hook-form";
import { Upload } from "antd";
import type { UploadProps } from "antd";
import { Icon } from "../../../components/Component";
import Swal from "sweetalert2";
import { nanoid } from "nanoid";
import dropdownApis from "../../../api/WorkOrderManagement/DropdownApis";

const { Text } = Typography;
const { Option } = Select;

interface SelectOptions {
  label: string;
  value: string;
  uom?: string;
  uomId?: string;
  category?: string;
  categoryId?: string;
  description?: string;
  availableQty?: number;
}

interface AllocatedMaterial {
  id: string;
  materialId: string;
  materialName: string;
  quantity: number;
  uom: string;
  uomId?: string;
  category?: string;
  categoryId?: string;
  notes?: string;
}

interface BudgetLine {
  lineId: number;
  headerId: number;
  budgetId: string;
  budgetLineName: string;
  budgetValue: number;
  allocatedLineValue: number;
  availableValue?: number; // Calculated field
  bom: string;
  budgetCategoryId?: number;
  customBudgetCategory?: string;
}

interface WorkOrderLineModalProps {
  modal: boolean;
  closeModal: () => void;
  append: (data: any) => void;
  update: (index: any, data: any) => void;
  formData: any;
  workLevel: string;
  materialBoqId: string;
  siteId: string;
  budgetId?: string; // From first step
  budgetAllocations?: { [key: number]: number }; // Track allocations by budget line ID
  onBudgetAllocation?: (
    budgetLineId: number,
    allocatedAmount: number,
    previousAmount?: number,
    previousBudgetLineId?: number
  ) => void;
  onEditModeExit?: (workLineItem: any, wasCancelled: boolean) => void;
}

const WorkOrderLineModal: React.FC<WorkOrderLineModalProps> = ({
  modal,
  closeModal,
  append,
  update,
  formData,
  workLevel,
  materialBoqId,
  siteId,
  budgetId,
  budgetAllocations = {},
  onBudgetAllocation,
  onEditModeExit,
}) => {
  const [workCategoryOptions, setWorkCategoryOptions] = useState<
    SelectOptions[]
  >([]);
  const [workSubCategoryOptions, setWorkSubCategoryOptions] = useState<
    SelectOptions[]
  >([]);
  const [uomOptions, setUomOptions] = useState<SelectOptions[]>([]);
  const [materialOptions, setMaterialOptions] = useState<SelectOptions[]>([]);
  const [towerVillaOptions, setTowerVillaOptions] = useState<SelectOptions[]>(
    []
  );
  const [uploadedDocs, setUploadedDocs] = useState<any[]>([]);

  // Budget line state
  const [budgetLines, setBudgetLines] = useState<BudgetLine[]>([]);
  const [selectedBudgetLineId, setSelectedBudgetLineId] = useState<
    number | null
  >(null);
  const [allocatedAmount, setAllocatedAmount] = useState<number>(0);
  const [budgetLineLoading, setBudgetLineLoading] = useState(false);

  // Track original values for edit mode
  const [originalBudgetLineId, setOriginalBudgetLineId] = useState<
    number | null
  >(null);
  const [originalAllocatedAmount, setOriginalAllocatedAmount] =
    useState<number>(0);

  // Material allocation state
  const [allocatedMaterials, setAllocatedMaterials] = useState<
    AllocatedMaterial[]
  >([]);
  const [selectedMaterialId, setSelectedMaterialId] = useState<string>("");
  const [materialQuantity, setMaterialQuantity] = useState<number>(1);
  const [editingMaterialId, setEditingMaterialId] = useState<string | null>(
    null
  );
  const [editingQuantity, setEditingQuantity] = useState<number>(0);

  const methods = useForm({
    mode: "onChange",
  });

  const {
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = methods;

  const workCategoryWatch = watch("workCategory");

  useEffect(() => {
    fetchWorkCategories();
    fetchUomOptions();

    // Only fetch tower/villa options if workLevel is "Tower / Block"
    if (workLevel === "Tower / Block" && siteId) {
      fetchTowerVillaOptions();
    } else {
      setTowerVillaOptions([]);
    }

    // Fetch budget lines if budgetId is provided
    if (budgetId) {
      fetchBudgetLines(parseInt(budgetId));
    }
  }, [workLevel, siteId, budgetId]);

  useEffect(() => {
    if (materialBoqId) {
      fetchMaterialOptions();
    } else {
      setMaterialOptions([]);
    }
  }, [materialBoqId]);

  useEffect(() => {
    if (workCategoryWatch) {
      fetchWorkSubCategories(workCategoryWatch);
    }
  }, [workCategoryWatch]);

  useEffect(() => {
    if (modal && formData?.item) {
      // Edit mode - populate form with existing data
      const item = formData.item;
      setValue("workCategory", item.workCategory || "");
      setValue("workSubCategory", item.workSubCategory || "");
      setValue("taskTitle", item.taskTitle || "");
      setValue("taskDescription", item.taskDescription || "");
      setValue("quantity", item.quantity || 0);
      setValue("uom", item.uom || "");
      setValue("towerVilla", item.towerVilla || "");
      setValue("floorName", item.floorName || "");
      setValue("flatName", item.flatName || "");
      setUploadedDocs(item.uploadedDocs || []);
      setAllocatedMaterials(item.allocatedMaterials || []);

      // Initialize budget line data if available
      if (item.budgetLineId) {
        // Ensure budgetLineId is converted to number
        const budgetLineIdNum = typeof item.budgetLineId === 'string' ? parseInt(item.budgetLineId) : item.budgetLineId;
        console.log("Edit mode - Setting budget line data:", {
          budgetLineId: item.budgetLineId,
          budgetLineIdNum,
          budgetLineName: item.budgetLineName,
          allocatedAmount: item.allocatedAmount,
        });
        setSelectedBudgetLineId(budgetLineIdNum);
        setAllocatedAmount(item.allocatedAmount || 0);
        // Store original values for edit mode tracking
        setOriginalBudgetLineId(budgetLineIdNum);
        setOriginalAllocatedAmount(item.allocatedAmount || 0);
      } else {
        setOriginalBudgetLineId(null);
        setOriginalAllocatedAmount(0);
      }
    } else {
      // Add mode - reset form
      reset();
      setUploadedDocs([]);
      setAllocatedMaterials([]);
      setSelectedMaterialId("");
      setMaterialQuantity(1);

      // Reset budget line data
      setSelectedBudgetLineId(null);
      setAllocatedAmount(0);
      setOriginalBudgetLineId(null);
      setOriginalAllocatedAmount(0);
    }
  }, [modal, formData, setValue, reset]);

  // Refetch budget lines when original values are set in edit mode
  useEffect(() => {
    if (budgetId && originalBudgetLineId && originalAllocatedAmount > 0) {
      fetchBudgetLines(parseInt(budgetId));
    }
  }, [originalBudgetLineId, originalAllocatedAmount, budgetId]);

  const fetchWorkCategories = async () => {
    try {
      const { data, status } = await dropdownApis.workCategoryDropDown();
      if (status && data) {
        const workCategoryOptions = data.map((item: any) => ({
          label: item?.workCategoryName,
          value: item?.workCategoryId?.toString(),
        }));
        setWorkCategoryOptions(workCategoryOptions);
      }
    } catch (error) {
      console.log("Error fetching work categories:", error);
      message.error("Failed to fetch work categories");
    }
  };

  const fetchWorkSubCategories = async (category: string) => {
    try {
      const subCategoryMap: { [key: string]: SelectOptions[] } = {
        Electrical: [
          { label: "Wiring", value: "Wiring" },
          { label: "Lighting", value: "Lighting" },
          { label: "Power", value: "Power" },
          { label: "Cabling", value: "Cabling" },
          { label: "Panel Installation", value: "Panel Installation" },
          { label: "Switch Installation", value: "Switch Installation" },
        ],
        Plumbing: [
          { label: "Water Supply", value: "Water Supply" },
          { label: "Drainage", value: "Drainage" },
          { label: "Sanitary", value: "Sanitary" },
          { label: "Pipe Installation", value: "Pipe Installation" },
          { label: "Fixture Installation", value: "Fixture Installation" },
        ],
        Civil: [
          { label: "Concrete Work", value: "Concrete Work" },
          { label: "Masonry", value: "Masonry" },
          { label: "Structural", value: "Structural" },
          { label: "Foundation", value: "Foundation" },
          { label: "Plastering", value: "Plastering" },
        ],
        Mechanical: [
          { label: "Equipment Installation", value: "Equipment Installation" },
          { label: "Machinery Setup", value: "Machinery Setup" },
          { label: "Maintenance", value: "Maintenance" },
        ],
        HVAC: [
          { label: "Duct Installation", value: "Duct Installation" },
          { label: "AC Installation", value: "AC Installation" },
          { label: "Ventilation", value: "Ventilation" },
        ],
        Painting: [
          { label: "Interior Painting", value: "Interior Painting" },
          { label: "Exterior Painting", value: "Exterior Painting" },
          { label: "Primer Application", value: "Primer Application" },
        ],
        Flooring: [
          { label: "Tile Installation", value: "Tile Installation" },
          { label: "Wood Flooring", value: "Wood Flooring" },
          { label: "Marble Installation", value: "Marble Installation" },
        ],
      };
      setWorkSubCategoryOptions(subCategoryMap[category] || []);
    } catch (error) {
      console.log("Error fetching work sub-categories:", error);
    }
  };

  const fetchUomOptions = async () => {
    try {
      const { data, status } = await dropdownApis.workUomDropDown();
      if (status && data) {
        const uomOptions = data.map((item: any) => ({
          label: item?.uomName,
          value: item?.workUomId,
        }));
        setUomOptions(uomOptions);
      }
    } catch (error) {
      console.log("Error fetching UOM options:", error);
      message.error("Failed to fetch UOM options");
    }
  };

  const fetchMaterialOptions = async () => {
    try {
      if (!materialBoqId) {
        setMaterialOptions([]);
        return;
      }

      const { response: data, status } =
        await dropdownApis.materialBoQMaterialLinesDrioDown(
          parseInt(materialBoqId)
        );
      if (status && data && data.lines) {
        const materialOptions = data.lines.map((item: any) => ({
          label: item?.materialName,
          value: item?.materialId?.toString(),
          uom: item?.uomName,
          uomId: item?.uomId?.toString(),
          category: item?.categoryName,
          categoryId: item?.categoryId?.toString(),
          description: item?.description || item?.materialDescription,
          availableQty: item?.remainingRequestedQty,
        }));
        setMaterialOptions(materialOptions);
      }
    } catch (error) {
      console.log("Error fetching material options:", error);
      message.error("Failed to fetch materials from Material BOQ");
    }
  };

  const fetchTowerVillaOptions = async () => {
    try {
      if (!siteId) {
        setTowerVillaOptions([]);
        return;
      }

      const { data, status } = await dropdownApis.fetchBlockBySiteId(
        parseInt(siteId)
      );
      if (status && data) {
        const towerVillaOptions = data.map((item: any) => ({
          label: item?.blockName,
          value: item?.blockID?.toString(),
        }));
        setTowerVillaOptions(towerVillaOptions);
      }
    } catch (error) {
      console.log("Error fetching tower/villa options:", error);
      message.error("Failed to fetch tower/villa options");
    }
  };

  const fetchBudgetLines = async (headerId: number) => {
    try {
      setBudgetLineLoading(true);
      const { data, status } = await dropdownApis.getBudgetLinesDropdown(
        headerId
      );
      if (status && data) {
        // Calculate available value for each budget line
        const budgetLinesWithAvailable = data.map((budgetLine: BudgetLine) => {
          const currentlyAllocated = budgetAllocations[budgetLine.lineId] || 0;
          let availableValue =
            budgetLine.budgetValue -
            budgetLine.allocatedLineValue -
            currentlyAllocated;

          // In edit mode, if this is the original budget line, add back the original allocated amount
          const blLineId = typeof budgetLine.lineId === 'string' ? parseInt(budgetLine.lineId) : budgetLine.lineId;
          const origLineId = typeof originalBudgetLineId === 'string' ? parseInt(originalBudgetLineId) : originalBudgetLineId;

          if (
            formData.action === "edit" &&
            origLineId === blLineId &&
            originalAllocatedAmount > 0
          ) {
            availableValue += originalAllocatedAmount;
            console.log(`Restored ${originalAllocatedAmount} to budget line ${blLineId} in fetchBudgetLines`);
          }

          return {
            ...budgetLine,
            availableValue: Math.max(0, availableValue), // Ensure non-negative
          };
        });
        setBudgetLines(budgetLinesWithAvailable);
        console.log("Budget lines loaded:", budgetLinesWithAvailable);
        console.log("Selected budget line ID:", selectedBudgetLineId);

        // Check if the selected budget line exists in the loaded lines
        const selectedLine = budgetLinesWithAvailable.find(
          (bl: any) => {
            const blLineId = typeof bl.lineId === 'string' ? parseInt(bl.lineId) : bl.lineId;
            const searchId = typeof selectedBudgetLineId === 'string' ? parseInt(selectedBudgetLineId) : selectedBudgetLineId;
            return blLineId === searchId;
          }
        );
        if (selectedLine) {
          console.log("Found selected budget line:", selectedLine);
        } else if (selectedBudgetLineId) {
          console.log("Selected budget line not found in loaded lines! Looking for:", selectedBudgetLineId,
            "Available lines:", budgetLinesWithAvailable.map((bl:any) => ({ lineId: bl.lineId, name: bl.budgetLineName })));
        }
      }
    } catch (error) {
      console.log("Error fetching budget lines:", error);
      message.error("Failed to fetch budget lines");
    } finally {
      setBudgetLineLoading(false);
    }
  };

  // Helper function to get available budget for a specific budget line
  const getAvailableBudget = (budgetLineId: number): number => {
    console.log("budgetLines", budgetLines, "searching for ID:", budgetLineId, typeof budgetLineId);

    // Ensure we're comparing numbers with numbers
    const budgetLine = budgetLines.find(
      (bl: any) => {
        const blLineId = typeof bl.lineId === 'string' ? parseInt(bl.lineId) : bl.lineId;
        const searchId = typeof budgetLineId === 'string' ? parseInt(budgetLineId) : budgetLineId;
        console.log("Comparing:", blLineId, "vs", searchId, "match:", blLineId === searchId);
        return blLineId === searchId;
      }
    );

    if (!budgetLine) {
      console.log(`Budget line not found for ID: ${budgetLineId}. Available lines:`,
        budgetLines.map(bl => ({ lineId: bl.lineId, name: bl.budgetLineName })));
      return 0;
    }

    console.log("budgetLine found:", budgetLine);
    console.log(`Getting available budget for line ${budgetLineId}:`, {
      budgetValue: budgetLine.budgetValue,
      allocatedLineValue: budgetLine.allocatedLineValue,
      availableValue: budgetLine.availableValue,
      formDataAction: formData.action,
      originalBudgetLineId,
      isEditMode: formData.action === "edit",
      isSameLine: originalBudgetLineId === budgetLineId,
    });

    // Always use the availableValue from the budget line as it's already calculated correctly
    // including any restoration done during edit mode
    return budgetLine.availableValue || 0;
  };

  // Material allocation methods
  const getAvailableMaterials = () => {
    const allocatedIds = allocatedMaterials.map(
      (material) => material.materialId
    );
    return materialOptions.filter(
      (option) => !allocatedIds.includes(option.value)
    );
  };

  const handleAddMaterial = () => {
    if (!selectedMaterialId) {
      message.warning("Please select a material first");
      return;
    }

    if (!materialQuantity || materialQuantity <= 0) {
      message.error("Quantity must be greater than 0");
      return;
    }

    if (
      allocatedMaterials.some(
        (material) => material.materialId === selectedMaterialId
      )
    ) {
      message.error("This material has already been allocated");
      return;
    }

    const materialToAdd = materialOptions.find(
      (material) => material.value === selectedMaterialId
    );

    if (!materialToAdd) {
      message.error("Selected material not found");
      return;
    }

    // Check if requested quantity exceeds available quantity
    const availableQty = materialToAdd.availableQty || 0;
    if (materialQuantity > availableQty) {
      message.error(
        `Cannot allocate ${materialQuantity} ${materialToAdd.uom}. Only ${availableQty} ${materialToAdd.uom} available.`
      );
      return;
    }

    const newAllocatedMaterial: AllocatedMaterial = {
      id: nanoid(),
      materialId: materialToAdd.value,
      materialName: materialToAdd.label,
      quantity: materialQuantity,
      uom: materialToAdd.uom || "nos",
      uomId: materialToAdd.uomId || materialToAdd.uom,
      category: materialToAdd.category,
      categoryId: materialToAdd.categoryId,
    };

    const updatedMaterials = [...allocatedMaterials, newAllocatedMaterial];
    setAllocatedMaterials(updatedMaterials);

    setSelectedMaterialId("");
    setMaterialQuantity(1);

    message.success(`${materialToAdd.label} added successfully`);
  };

  const handleRemoveMaterial = (materialId: string) => {
    const materialToRemove = allocatedMaterials.find(
      (m) => m.id === materialId
    );
    const updatedMaterials = allocatedMaterials.filter(
      (material) => material.id !== materialId
    );

    setAllocatedMaterials(updatedMaterials);
    message.success(`${materialToRemove?.materialName} removed successfully`);
  };

  const handleStartEdit = (materialId: string, currentQuantity: number) => {
    setEditingMaterialId(materialId);
    setEditingQuantity(currentQuantity);
  };

  const handleSaveEdit = (materialId: string) => {
    if (editingQuantity <= 0) {
      message.error("Quantity must be greater than 0");
      return;
    }

    const updatedMaterials = allocatedMaterials.map((material) =>
      material.id === materialId
        ? { ...material, quantity: editingQuantity }
        : material
    );

    setAllocatedMaterials(updatedMaterials);
    setEditingMaterialId(null);
    message.success("Quantity updated successfully");
  };

  const handleCancelEdit = () => {
    setEditingMaterialId(null);
    setEditingQuantity(0);
  };

  // Table columns for material allocation
  const materialColumns: ColumnsType<AllocatedMaterial> = [
    {
      title: "Material Name",
      dataIndex: "materialName",
      key: "materialName",
      width: "40%",
      render: (name: string, record: AllocatedMaterial) => (
        <div>
          <Text strong>{name}</Text>
          {record.category && (
            <div style={{ marginTop: 4 }}>
              <Tag color="blue">{record.category}</Tag>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: "25%",
      render: (quantity: number, record: AllocatedMaterial) => {
        const isEditing = editingMaterialId === record.id;

        return isEditing ? (
          <Space>
            <InputNumber
              min={0.01}
              step={0.01}
              value={editingQuantity}
              onChange={(value) => setEditingQuantity(value || 0)}
              style={{ width: "80px" }}
              size="small"
            />
            <AntButton
              type="text"
              icon={<CheckOutlined />}
              onClick={() => handleSaveEdit(record.id)}
              size="small"
              style={{ color: "#52c41a" }}
            />
            <AntButton
              type="text"
              icon={<CloseOutlined />}
              onClick={handleCancelEdit}
              size="small"
              style={{ color: "#ff4d4f" }}
            />
          </Space>
        ) : (
          <Space>
            <Text>{quantity}</Text>
            <AntButton
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleStartEdit(record.id, quantity)}
              size="small"
            />
          </Space>
        );
      },
    },
    {
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      width: "15%",
      render: (uom: string) => <Tag color="geekblue">{uom}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      width: "20%",
      render: (_, record: AllocatedMaterial) => (
        <Popconfirm
          title="Remove Material"
          description={`Are you sure you want to remove ${record.materialName}?`}
          onConfirm={() => handleRemoveMaterial(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <AntButton type="text" danger icon={<DeleteOutlined />} size="small">
            Remove
          </AntButton>
        </Popconfirm>
      ),
    },
  ];

  const uploadProps: UploadProps = {
    name: "file",
    multiple: true,
    accept: ".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png",
    beforeUpload: (file) => {
      const isValidType = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "image/jpeg",
        "image/png",
        "image/jpg",
      ].includes(file.type);

      if (!isValidType) {
        Swal.fire({
          icon: "error",
          title: "Invalid File Type",
          text: "Only PDF, DOC, XLS, and Image files are allowed",
        });
        return false;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        Swal.fire({
          icon: "error",
          title: "File Too Large",
          text: "File must be smaller than 10MB",
        });
        return false;
      }

      return false;
    },
    onChange: (info) => {
      setUploadedDocs(info.fileList);
    },
    fileList: uploadedDocs,
  };

  const validateForm = (data: any) => {
    if (!data.workCategory?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Work Category is required",
      });
      return false;
    }

    if (!data.taskTitle?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Task Title is required",
      });
      return false;
    }

    if (data.taskTitle.length > 150) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Task Title cannot exceed 150 characters",
      });
      return false;
    }

    if (!data.taskDescription?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Task Description is required",
      });
      return false;
    }

    if (data.taskDescription.length > 500) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Task Description cannot exceed 500 characters",
      });
      return false;
    }

    if (!data.quantity || data.quantity <= 0) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Quantity must be greater than 0",
      });
      return false;
    }

    if (!data.uom) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Unit of Measurement is required",
      });
      return false;
    }

    // Validate allocated materials
    for (const material of allocatedMaterials) {
      if (!material.quantity || material.quantity <= 0) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: `Material "${material.materialName}" must have quantity greater than 0`,
        });
        return false;
      }
    }

    // Check location fields based on work level
    // if (["Tower", "Villa"].includes(workLevel)) {
    //   if (!data.towerVilla) {
    //     Swal.fire({
    //       icon: "error",
    //       title: "Validation Error",
    //       text: "Tower/Block selection is required for selected work level",
    //     });
    //     return false;
    //   }
    // }

    if (workLevel === "Floor" && !data.floorName?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Floor Name is required for Floor level work",
      });
      return false;
    }

    if (workLevel === "Flat" && !data.flatName?.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Flat Name is required for Flat level work",
      });
      return false;
    }

    // Validate budget allocation if budget lines are available
    if (budgetId && budgetLines.length > 0) {
      if (!selectedBudgetLineId) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Budget Line selection is required",
        });
        return false;
      }

      if (!allocatedAmount || allocatedAmount <= 0) {
        Swal.fire({
          icon: "error",
          title: "Validation Error",
          text: "Allocated amount must be greater than 0",
        });
        return false;
      }

      const availableBudget = getAvailableBudget(selectedBudgetLineId);
      if (allocatedAmount > availableBudget) {
        Swal.fire({
          icon: "error",
          title: "Budget Exceeded",
          text: `Allocated amount (₹${allocatedAmount.toLocaleString()}) exceeds available budget (₹${availableBudget.toLocaleString()})`,
        });
        return false;
      }
    }

    return true;
  };

  const onSubmit = (data: any) => {
    if (!validateForm(data)) return;

    // Get display names from options
    const selectedWorkCategory = workCategoryOptions.find(
      (opt) => opt.value === data.workCategory
    );
    const selectedWorkSubCategory = workSubCategoryOptions.find(
      (opt) => opt.value === data.workSubCategory
    );
    const selectedUom = uomOptions.find((opt) => opt.value === data.uom);

    // Map allocated materials with UOM names
    const mappedAllocatedMaterials = allocatedMaterials.map((material) => {
      const materialOption = materialOptions.find(
        (opt) => opt.value === material.materialId
      );
      const materialUom = uomOptions.find((opt) => opt.value === material.uom);

      return {
        ...material,
        uomName: materialUom?.label || material.uom, // Store UOM name for display
      };
    });

    // Get budget line display name
    const selectedBudgetLine = budgetLines.find(
      (bl) => bl.lineId === selectedBudgetLineId
    );

    const lineData = {
      id: formData?.item?.id || nanoid(),
      workCategory: data.workCategory,
      workCategoryName: selectedWorkCategory?.label || data.workCategory,
      workSubCategory: data.workSubCategory,
      workSubCategoryName:
        selectedWorkSubCategory?.label || data.workSubCategory,
      taskTitle: data.taskTitle,
      taskDescription: data.taskDescription,
      quantity: Number(data.quantity),
      uom: data.uom,
      uomName: selectedUom?.label || data.uom,
      allocatedMaterials: mappedAllocatedMaterials,
      towerVilla: data.towerVilla,
      floorName: data.floorName,
      flatName: data.flatName,
      uploadedDocs: uploadedDocs,
      // Budget line information
      budgetLineId: selectedBudgetLineId,
      budgetLineName: selectedBudgetLine?.budgetLineName || "",
      allocatedAmount: allocatedAmount,
      budgetValue: selectedBudgetLine?.budgetValue || 0,
    };

    // Handle budget allocation tracking
    if (selectedBudgetLineId && onBudgetAllocation) {
      if (formData.action === "edit") {
        // Edit mode: Pass original budget line ID and amount for proper tracking
        onBudgetAllocation(
          selectedBudgetLineId,
          allocatedAmount,
          originalAllocatedAmount,
          originalBudgetLineId || undefined
        );
      } else {
        // Add mode: Simple allocation
        onBudgetAllocation(selectedBudgetLineId, allocatedAmount);
      }
    }

    // Clean up edit mode tracking in parent
    if (formData.action === "edit" && onEditModeExit) {
      onEditModeExit(formData.item, false);
    }

    if (formData.action === "edit" && formData.index !== null) {
      update(formData.index, lineData);
    } else {
      append(lineData);
    }

    closeModal();

    Swal.fire({
      icon: "success",
      title: "Success",
      text: `Work order line ${
        formData.action === "edit" ? "updated" : "added"
      } successfully`,
      timer: 1500,
      showConfirmButton: false,
    });
  };

  const availableForSelection = getAvailableMaterials();

  return (
    <Modal
      isOpen={modal}
      toggle={closeModal}
      size="xl"
      className="modal-dialog-centered"
    >
      <ModalHeader toggle={closeModal}>
        {formData.action === "edit"
          ? "Edit Work Order Line"
          : "Add Work Order Line"}
      </ModalHeader>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <Row className="gy-3">
              <Col lg="6">
                <FormSelect
                  name="workCategory"
                  label="Work Category"
                  options={workCategoryOptions}
                  required={true}
                  placeholder="Select Work Category"
                  tooltipText="Major category of work (e.g., Electrical, Plumbing)"
                />
              </Col>
              {/* <Col lg="6">
                <FormSelect
                  name="workSubCategory"
                  label="Work Sub Category"
                  options={workSubCategoryOptions}
                  placeholder="Select Sub Category (Optional)"
                  tooltipText="Subdivision under selected category"
                />
              </Col> */}

              <Col lg="6">
                <FormInput
                  name="taskTitle"
                  label="Task Title"
                  required={true}
                  placeholder="Enter task title"
                  maxLength={150}
                  tooltipText="Title or summary of task"
                />
              </Col>

              <Col lg="12">
                <FormTextArea
                  name="taskDescription"
                  label="Task Description"
                  required={true}
                  placeholder="Enter detailed task description (Max 500 characters)"
                  maxLength={500}
                  rows={3}
                />
              </Col>

              <Col lg="6">
                <FormInput
                  name="quantity"
                  label="Quantity"
                  type="number"
                  required={true}
                  placeholder="Enter quantity"
                  min={0.01}
                  tooltipText="Estimated quantity of the task"
                />
              </Col>
              <Col lg="6">
                <FormSelect
                  name="uom"
                  label="Unit of Measurement"
                  options={uomOptions}
                  required={true}
                  placeholder="Select UOM"
                  tooltipText="Unit of Measurement"
                />
              </Col>

              {/* Budget Line Selection - Show only if budget is selected in step 1 */}
              {budgetId && budgetLines.length > 0 && (
                <>
                  <Col lg="6">
                    <div className="form-group">
                      <label className="form-label">
                        Budget Line <span className="text-danger">*</span>
                      </label>
                      <Select
                        placeholder="Select Budget Line"
                        value={
                          budgetLines.length > 0 && selectedBudgetLineId
                            ? selectedBudgetLineId
                            : undefined
                        }
                        onChange={(value) => {
                          console.log("Budget line changed to:", value);
                          setSelectedBudgetLineId(value);
                          setAllocatedAmount(0); // Reset allocation when budget line changes
                        }}
                        loading={budgetLineLoading}
                        style={{ width: "100%", minHeight: "38px" }}
                        dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                        showSearch
                        filterOption={(input, option) =>
                          String(option?.label || "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        optionLabelProp="label"
                        notFoundContent={
                          budgetLineLoading
                            ? "Loading..."
                            : "No budget lines found"
                        }
                      >
                        {budgetLines.map((budgetLine) => (
                          <Option
                            key={budgetLine.lineId}
                            value={budgetLine.lineId}
                            label={budgetLine.budgetLineName}
                            disabled={budgetLine.availableValue === 0}
                          >
                            <div style={{ padding: "4px 0" }}>
                              <div
                                style={{
                                  fontWeight: "500",
                                  fontSize: "14px",
                                  marginBottom: "2px",
                                }}
                              >
                                {budgetLine.budgetLineName}
                              </div>
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#666",
                                  lineHeight: "1.2",
                                }}
                              >
                                <span>
                                  Budget: ₹
                                  {budgetLine.budgetValue.toLocaleString()}
                                </span>
                                <span
                                  style={{ margin: "0 8px", color: "#ddd" }}
                                >
                                  |
                                </span>
                                <span
                                  style={{
                                    color:
                                      budgetLine.availableValue === 0
                                        ? "#ff4d4f"
                                        : "#52c41a",
                                    fontWeight: "500",
                                  }}
                                >
                                  Available: ₹
                                  {budgetLine.availableValue?.toLocaleString() ||
                                    0}
                                </span>
                              </div>
                              {budgetLine.availableValue === 0 && (
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "#ff4d4f",
                                    marginTop: "2px",
                                  }}
                                >
                                  No budget available
                                </div>
                              )}
                            </div>
                          </Option>
                        ))}
                      </Select>
                      {budgetLines.length === 0 && !budgetLineLoading && (
                        <small className="form-text text-muted">
                          No budget lines available for the selected budget
                        </small>
                      )}
                    </div>
                  </Col>

                  <Col lg="6">
                    <div className="form-group">
                      <label className="form-label">
                        Allocated Amount (₹){" "}
                        <span className="text-danger">*</span>
                      </label>
                      <InputNumber
                        placeholder="Enter allocated amount"
                        value={allocatedAmount}
                        onChange={(value) => setAllocatedAmount(value || 0)}
                        min={0}
                        max={
                          selectedBudgetLineId
                            ? getAvailableBudget(selectedBudgetLineId)
                            : undefined
                        }
                        precision={2}
                        formatter={(value) =>
                          `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                        }
                        parser={(value) =>
                          parseFloat(value!.replace(/₹\s?|(,*)/g, "")) || 0
                        }
                        style={{ width: "100%" }}
                        disabled={!selectedBudgetLineId}
                      />
                      {selectedBudgetLineId && (
                        <small className="form-text text-muted">
                          Available budget: ₹
                          {getAvailableBudget(
                            selectedBudgetLineId
                          ).toLocaleString()}
                        </small>
                      )}
                    </div>
                  </Col>
                </>
              )}

              {["Tower / Block"].includes(workLevel) && (
                <Col lg="6">
                  <FormSelect
                    name="towerVilla"
                    label="Tower/Block Selection"
                    options={towerVillaOptions}
                    required={true}
                    placeholder="Select Tower/Block"
                    tooltipText="Based on selected segment level"
                  />
                </Col>
              )}

              {workLevel === "Villa" && (
                <Col lg="6">
                  <FormInput
                    name="Villa"
                    label="Villa"
                    required={true}
                    placeholder="Enter Villa"
                    tooltipText="Name of the Villa"
                  />
                </Col>
              )}

              {workLevel === "Floor" && (
                <Col lg="6">
                  <FormInput
                    name="floorName"
                    label="Floor Name"
                    required={true}
                    placeholder="Enter floor name"
                    tooltipText="Name of the floor if applicable"
                  />
                </Col>
              )}

              {workLevel === "Flat" && (
                <Col lg="6">
                  <FormInput
                    name="flatName"
                    label="Flat Name"
                    required={true}
                    placeholder="Enter flat name"
                    tooltipText="Name of flat/unit if applicable"
                  />
                </Col>
              )}

              {/* Enhanced Material Allocation Section */}
              <Col lg="12">
                <div style={{ marginBottom: "16px" }}>
                  <label className="form-label d-flex align-items-center">
                    Material Allocation (Optional)
                  </label>
                  <small className="text-muted">
                    Select materials from the linked Material BOQ
                  </small>
                </div>

                {/* Add Material Section */}
                <Card
                  size="small"
                  title="Add New Material"
                  style={{ marginBottom: "16px" }}
                  bodyStyle={{ padding: "16px" }}
                >
                  <Row gutter={16} align="bottom">
                    <Col xs={24} sm={6} md={6}>
                      <label className="form-label mb-1">Select Material</label>
                      <Select
                        placeholder="Choose material to allocate"
                        value={selectedMaterialId || undefined}
                        onChange={setSelectedMaterialId}
                        showSearch
                        filterOption={(input, option) =>
                          option?.children
                            ?.toString()
                            .toLowerCase()
                            .includes(input.toLowerCase()) || false
                        }
                        disabled={availableForSelection.length === 0}
                        style={{ width: "100%" }}
                      >
                        {availableForSelection.map((material) => (
                          <Option key={material.value} value={material.value}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                padding: "2px 0",
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <Text style={{ fontSize: "13px" }}>
                                  {material.label}
                                </Text>
                                <Text
                                  type="secondary"
                                  style={{ marginLeft: 6, fontSize: "11px" }}
                                >
                                  ({material.uom})
                                </Text>
                                {material.category && (
                                  <Tag
                                    color="blue"
                                    style={{
                                      marginLeft: 6,
                                      fontSize: "10px",
                                      padding: "0 4px",
                                    }}
                                  >
                                    {material.category}
                                  </Tag>
                                )}
                              </div>
                              <div
                                style={{ textAlign: "right", minWidth: "80px" }}
                              >
                                <Text
                                  type="success"
                                  style={{
                                    fontSize: "10px",
                                    fontWeight: "600",
                                  }}
                                >
                                  Avail: {material.availableQty || 0}
                                </Text>
                              </div>
                            </div>
                          </Option>
                        ))}
                      </Select>
                    </Col>

                    <Col xs={12} sm={4} md={4}>
                      <label className="form-label  mb-1">Quantity</label>
                      <InputNumber
                        min={0.01}
                        step={0.01}
                        value={materialQuantity}
                        onChange={(value) => setMaterialQuantity(value || 1)}
                        placeholder="Enter qty"
                        style={{ width: "100%" }}
                      />
                    </Col>

                    <Col xs={12} sm={2} md={2}>
                      <AntButton
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddMaterial}
                        disabled={
                          !selectedMaterialId ||
                          !materialQuantity ||
                          materialQuantity <= 0
                        }
                        style={{ width: "100%", marginTop: "32px" }}
                      >
                        Add
                      </AntButton>
                    </Col>
                  </Row>

                  {availableForSelection.length === 0 && (
                    <Alert
                      message="No materials available"
                      description="All available materials have been allocated or no materials are available."
                      type="info"
                      showIcon
                      style={{ marginTop: 16 }}
                    />
                  )}
                </Card>

                {/* Allocated Materials Table */}
                <Card
                  size="small"
                  title={`Allocated Materials (${allocatedMaterials.length})`}
                >
                  {allocatedMaterials.length > 0 ? (
                    <Table
                      columns={materialColumns}
                      dataSource={allocatedMaterials}
                      rowKey="id"
                      pagination={false}
                      size="small"
                      scroll={{ x: 600 }}
                    />
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <span>
                          No materials allocated yet
                          <br />
                          <Text type="secondary" style={{ fontSize: "12px" }}>
                            Use the form above to add materials
                          </Text>
                        </span>
                      }
                      style={{ padding: "40px 20px" }}
                    />
                  )}
                </Card>

                {allocatedMaterials.length > 0 && (
                  <div className="mt-2">
                    <small className="text-success">
                      <Icon name="check-circle" className="me-1" />
                      {allocatedMaterials.length} material
                      {allocatedMaterials.length > 1 ? "s" : ""} allocated
                    </small>
                  </div>
                )}
              </Col>

              <Col lg="12" className="mb-3">
                <label className="form-label">
                  Upload Documents (Optional)
                </label>
                <Upload.Dragger {...uploadProps}>
                  <p className="ant-upload-drag-icon">
                    <Icon name="upload" />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag files to upload technical documents/drawings
                  </p>
                  <p className="ant-upload-hint">
                    Supports: PDF, DOC, XLS, Images (Max 10MB each)
                  </p>
                </Upload.Dragger>
              </Col>
            </Row>
            {/* <Row>
              {uploadedDocs && uploadedDocs.length > 0 && (
                <div className="mt-2">
                  <small className="text-success">
                    {uploadedDocs.length} file(s) selected
                  </small>
                </div>
              )}
            </Row> */}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button color="primary" type="submit">
              {formData.action === "edit" ? "Update Line" : "Add Line"}
            </Button>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default WorkOrderLineModal;
